import React from 'react';
import { useAuth } from '../../context/AuthContext';
import { T as DS } from '../../design-system/tokens';
import { AoneAvatar } from '../../design-system';
import { T } from '../../design-system';
import { APP_NAME } from '../../config/brand';

// The bronze bar was the loudest thing on every screen and forced white text
// and white glyphs, which is what made the bell and back arrow hard to read.
// A paper bar with ink content matches the rest of the app, gives real
// contrast, and lets the burgundy accent mean "action" instead of competing
// with a brown field.
const SHELL      = DS.surface;                 // paper, not bronze
const SHELL_DARK = DS.surfaceAlt;
const SHELL_TEXT = DS.text;                    // ink on paper

export default function MobileHeader({ pageTitle, T, unreadCount, onNotifToggle, notifRef, profilePhoto, onProfileClick, role, onBack, onMenu }) {
  const { user } = useAuth();
  const hideNotifications = role === 'security_guard';

  return (
    <>
    <style>{`
      .aone-mhpress { transition: transform 0.12s ease, opacity 0.12s ease; }
      .aone-mhpress:active { transform: scale(0.88); opacity: 0.7; }
      @media (prefers-reduced-motion: reduce) {
        .aone-mhpress { transition: none !important; }
        .aone-mhpress:active { transform: none !important; }
      }
    `}</style>
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 900,
      background: SHELL,
      borderBottom: `1px solid ${DS.divider}`,
      paddingTop: 'env(safe-area-inset-top, 0px)',
      fontFamily: DS.font,
      WebkitTapHighlightColor: 'transparent',
      boxShadow: DS.shadow.sm,
    }}>
      {/* 48px content row — SAP Fiori 3rem standard */}
      <div style={{
        height: 48,
        display: 'flex',
        alignItems: 'center',
        padding: '0 4px',
        gap: 2,
      }}>

        {/* ── Leading: back or logo ── */}
        <div style={{ width: 52, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {onBack ? (
            <button onClick={onBack} className="aone-mhpress" style={{
              background: 'none', border: 'none',
              width: 44, height: 44,
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: SHELL_TEXT,
              borderRadius: DS.r.sm,
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 5l-7 7 7 7"/>
              </svg>
            </button>
          ) : (
            <button onClick={onMenu} className="aone-mhpress" aria-label="Open menu" style={{
              background: 'none', border: 'none', padding: 0,
              width: 44, height: 44,
              cursor: onMenu ? 'pointer' : 'default',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: DS.r.sm,
            }}>
              {/* SAP Fiori-style grid/waffle icon — opens the module finder */}
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="3" width="7" height="7" rx="1.5" fill={SHELL_TEXT} opacity="0.85"/>
                <rect x="14" y="3" width="7" height="7" rx="1.5" fill={SHELL_TEXT} opacity="0.85"/>
                <rect x="3" y="14" width="7" height="7" rx="1.5" fill={SHELL_TEXT} opacity="0.85"/>
                <rect x="14" y="14" width="7" height="7" rx="1.5" fill={SHELL_TEXT} opacity="0.85"/>
              </svg>
            </button>
          )}
        </div>

        {/* ── Title: left-aligned (Fiori convention) ── */}
        <div style={{ flex: 1, overflow: 'hidden', paddingRight: 4 }}>
          {onBack ? (
            <span style={{
              fontSize: DS.type.md, fontWeight: 600, color: SHELL_TEXT,
              letterSpacing: '-0.01em',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              display: 'block', lineHeight: 1.25,
            }}>
              {pageTitle}
            </span>
          ) : (
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
              <span style={{ fontSize: DS.type.lg, fontWeight: 700, color: SHELL_TEXT, letterSpacing: '-0.02em', lineHeight: 1 }}>{APP_NAME}</span>
            </div>
          )}
        </div>

        {/* ── Trailing actions ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 0, paddingRight: 4, flexShrink: 0 }}>
          {!hideNotifications && (
            <div
              ref={notifRef}
              className="aone-mhpress"
              data-notif-toggle="1"
              onClick={onNotifToggle}
              style={{
                position: 'relative', cursor: 'pointer',
                width: 44, height: 44,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                borderRadius: DS.r.sm,
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={SHELL_TEXT} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.90 }}>
                <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                <path d="M13.73 21a2 2 0 01-3.46 0"/>
              </svg>
              {unreadCount > 0 && (
                <div style={{
                  position: 'absolute', top: 8, right: 6,
                  minWidth: 16, height: 16, borderRadius: 8, padding: '0 3px',
                  background: T.danger,
                  border: `2px solid ${DS.surface}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: DS.type.xxs, fontWeight: 700, color: '#fff',
                }}>
                  {unreadCount > 9 ? '9+' : unreadCount}
                </div>
              )}
            </div>
          )}

          {/* Avatar button */}
          <div
            onClick={onProfileClick}
            className="aone-mhpress"
            style={{
              width: 44, height: 44, cursor: 'pointer', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: DS.r.sm,
            }}
          >
            <AoneAvatar name={user?.name} seed={user?.email || user?.name} src={profilePhoto} size="sm"
              style={{ border: `1.5px solid ${DS.border}` }} />
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
